import { CsvValidationError } from '@/components/common/Validations/CsvValidations';
import { checkRosterCsv } from '@/components/common/Validations/checkRosterCsv';

export interface ExpectedFile {
  name: string;
  present: boolean;
}

const ROSTER_FILE_PATTERN = /^(students|teachers)_(\d{8})\.csv$/i;

// case-sensitive: a wrongly cased file is never consumed by a sync
const CONSUMABLE_FILE_PATTERN = /^(students|teachers)_(\d{8})\.csv$/;

const expectedDatestamp = (expectedFiles: ExpectedFile[]) => (
  expectedFiles[0]?.name.match(/_(\d{8})\.csv$/)?.[1] ?? null
);

export interface FileAnnotation {
  level: 'muted' | 'warning';
  message: string;
}

export const fileAnnotation = (name: string, expectedFiles: ExpectedFile[]): FileAnnotation | null => {
  if (expectedFiles.some(expected => expected.name === name)) return null;

  const basename = name.split('/').pop() ?? '';
  const match = basename.match(ROSTER_FILE_PATTERN);

  if (!match) {
    if (!name.includes('/') && /\.csv$/i.test(basename)) {
      return { level: 'warning', message: "Won't be picked up — not a recognized roster filename." };
    }

    return null;
  }

  if (name.includes('/')) {
    return { level: 'warning', message: "Won't be picked up — the sync only reads the folder root." };
  }

  if (!CONSUMABLE_FILE_PATTERN.test(basename)) {
    return { level: 'warning', message: "Won't be picked up — the filename must exactly match, all lowercase." };
  }

  const datestamp = expectedDatestamp(expectedFiles);
  if (datestamp && match[2] < datestamp) {
    return { level: 'muted', message: 'Old — dated for a previous sync.' };
  }
  if (datestamp && match[2] > datestamp) {
    return { level: 'warning', message: "Won't be picked up until a later sync — future datestamp." };
  }

  return null;
};

// true when the file's datestamp is current or future — i.e. a run-now, tonight's, or a
// later sync will read it. Stale datestamps and non-roster names are never consumed.
export const syncWillConsume = (name: string, runNowExpectedFiles: ExpectedFile[]): boolean => {
  if (name.includes('/')) return false;

  const match = name.match(CONSUMABLE_FILE_PATTERN);
  if (!match) return false;

  const floor = expectedDatestamp(runNowExpectedFiles);

  return floor !== null && match[2] >= floor;
};

export type RosterImportType = 'staff' | 'student';

export const rosterImportType = (name: string): RosterImportType | null => {
  const basename = name.split('/').pop() ?? '';
  if (/^teacher/i.test(basename)) return 'staff';
  if (/^student/i.test(basename)) return 'student';

  return null;
};

// for files whose name gives no type hint, guess the schema from the header row
export const sniffImportType = (text: string): RosterImportType => {
  const header = (text.split(/[\r\n]+/g)[0] ?? '').toLowerCase();

  return header.includes('username') ? 'student' : 'staff';
};

export const runRosterValidations = (
  text: string,
  importType: RosterImportType,
  programCodes: string[],
  strictPasswordPolicies: string[],
): CsvValidationError[] => checkRosterCsv(text, importType, programCodes, strictPasswordPolicies).errors;
